import React, { useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Stack,
    IconButton,
    MobileStepper,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    Menu,
    MenuItem
} from '@mui/material';
import {
    KeyboardArrowLeft,
    KeyboardArrowRight,
    FitnessCenter,
    PlayArrow,
    CheckCircle,
    Delete as DeleteIcon,
    Undo as UndoIcon
} from '@mui/icons-material';
import { useLanguage } from '../../i18n/LanguageContext';

interface AlternativeExercise {
    exercise: string;
    sets: number;
    reps: string;
    notes: string;
}

interface WorkoutRecommendation {
    exercise: string;
    sets: number;
    reps: string;
    notes: string;
    alternatives?: AlternativeExercise[];
    skipped?: boolean;
}

interface WorkoutReviewWizardProps {
    recommendation: WorkoutRecommendation[];
    onSave: (editedRecommendation: WorkoutRecommendation[]) => void;
    onBack: () => void;
    onCancel: () => void;
}

export const WorkoutReviewWizard: React.FC<WorkoutReviewWizardProps> = ({
    recommendation,
    onSave,
    onBack,
    onCancel
}) => {
    const { t } = useLanguage();

    const [activeStep, setActiveStep] = useState(0);
    const [editedWorkout, setEditedWorkout] = useState<WorkoutRecommendation[]>(JSON.parse(JSON.stringify(recommendation)));

    // Steps: 0 = Overview, 1..N = Exercises, N+1 = Summary
    const totalSteps = editedWorkout.length + 2;

    const handleNext = () => {
        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
    };

    const handleFinish = () => {
        // Filter out skipped exercises before saving
        const finalWorkout = editedWorkout.filter(ex => !ex.skipped);
        onSave(finalWorkout);
    };

    const renderOverviewStep = () => (
        <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h5" gutterBottom align="center" fontWeight="bold">
                {t('review.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" paragraph>
                {t('review.overview.subtitle')}
            </Typography>

            <List sx={{ flexGrow: 1, overflowY: 'auto', mb: 2 }}>
                {editedWorkout.map((item, index) => (
                    <React.Fragment key={index}>
                        <ListItem
                            sx={{
                                opacity: item.skipped ? 0.5 : 1,
                                textDecoration: item.skipped ? 'line-through' : 'none',
                                bgcolor: item.skipped ? 'action.hover' : 'inherit'
                            }}
                            secondaryAction={
                                item.skipped && (
                                    <Button size="small" onClick={() => handleToggleSkip(index + 1)}>
                                        {t('aiWizard.button.undo')}
                                    </Button>
                                )
                            }
                        >
                            <ListItemIcon>
                                <FitnessCenter color={item.skipped ? "disabled" : "primary"} />
                            </ListItemIcon>
                            <ListItemText
                                primary={item.exercise}
                                secondary={item.skipped ? t('review.step.skipped') : `${item.sets} ${t('review.step.sets')} x ${item.reps} ${t('review.step.reps')}`}
                            />
                        </ListItem>
                        {index < editedWorkout.length - 1 && <Divider variant="inset" component="li" />}
                    </React.Fragment>
                ))}
            </List>

            <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                endIcon={<PlayArrow />}
                onClick={handleNext}
                sx={{ mt: 'auto' }}
            >
                {t('review.button.start')}
            </Button>

            <Button
                size="small"
                onClick={onBack}
                sx={{ mt: 1, mx: 'auto', display: 'block' }}
            >
                {t('recommendation.generateNew')}
            </Button>
        </Box>
    );

    // State for swapping
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleUpdateExercise = (stepIndex: number, field: 'sets' | 'reps', value: string | number) => {
        const newWorkout = [...editedWorkout];
        // stepIndex 1 corresponds to editedWorkout[0]
        const exerciseIndex = stepIndex - 1;

        if (field === 'sets') {
            newWorkout[exerciseIndex].sets = Number(value);
        } else {
            newWorkout[exerciseIndex].reps = String(value);
        }
        setEditedWorkout(newWorkout);
    };

    const handleSwapClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleSwapClose = () => {
        setAnchorEl(null);
    };

    const handleSwapExercise = (alternative: AlternativeExercise) => {
        const exerciseIndex = activeStep - 1;
        const newWorkout = [...editedWorkout];
        const oldExercise = newWorkout[exerciseIndex];
        const currentAlternatives = newWorkout[exerciseIndex].alternatives || [];

        // Swap
        // 1. Remove the selected alternative from the alternates list
        const newAlternatives = currentAlternatives.filter(a => a.exercise !== alternative.exercise);

        // 2. Add the OLD exercise to the alternatives list (demote it)
        newAlternatives.push({
            exercise: oldExercise.exercise,
            sets: oldExercise.sets,
            reps: oldExercise.reps,
            notes: oldExercise.notes
        });

        // 3. Update the main exercise with the alternative's details
        newWorkout[exerciseIndex] = {
            ...newWorkout[exerciseIndex],
            exercise: alternative.exercise,
            sets: alternative.sets,
            reps: alternative.reps,
            notes: alternative.notes,
            alternatives: newAlternatives
        };

        setEditedWorkout(newWorkout);
        handleSwapClose();
    };

    const handleToggleSkip = (stepIndex?: number) => {
        // use stepIndex if provided (for overview), otherwise use activeStep
        const exerciseIndex = (stepIndex !== undefined ? stepIndex : activeStep) - 1;
        const newWorkout = [...editedWorkout];
        const isSkipping = !newWorkout[exerciseIndex].skipped;

        newWorkout[exerciseIndex] = {
            ...newWorkout[exerciseIndex],
            skipped: isSkipping
        };

        setEditedWorkout(newWorkout);

        // If we are skipping from the detail view, maybe auto-advance?
        // User requested "something as skip", so let's advance if skipping
        if (isSkipping && stepIndex === undefined && activeStep < totalSteps - 1) {
            handleNext();
        }
    };


    const renderExerciseStep = (index: number) => {
        const exercise = editedWorkout[index];
        // Safe check
        if (!exercise) return null;

        return (
            <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="h6" align="center" gutterBottom color={exercise.skipped ? "text.secondary" : "primary"} fontWeight="bold" sx={{ textDecoration: exercise.skipped ? 'line-through' : 'none' }}>
                    #{index + 1} {exercise.exercise}
                </Typography>

                {exercise.skipped ? (
                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                        <Typography variant="h5" color="text.secondary" gutterBottom>
                            {t('review.step.skipped')}
                        </Typography>
                        <Button
                            variant="outlined"
                            startIcon={<UndoIcon />}
                            onClick={() => handleToggleSkip()}
                            sx={{ mt: 2 }}
                        >
                            {t('aiWizard.button.undo')}
                        </Button>
                    </Box>
                ) : (
                    <>
                        <Card variant="outlined" sx={{ width: '100%', maxWidth: 400, mb: 3, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <CardContent>
                                <Stack spacing={4}>
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            {t('review.step.sets')}
                                        </Typography>
                                        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                                            <IconButton
                                                onClick={() => handleUpdateExercise(activeStep, 'sets', Math.max(1, exercise.sets - 1))}
                                                color="primary"
                                                size="large"
                                                sx={{ border: '1px solid', borderColor: 'divider' }}
                                            >
                                                -
                                            </IconButton>
                                            <Typography variant="h4" fontWeight="bold">{exercise.sets}</Typography>
                                            <IconButton
                                                onClick={() => handleUpdateExercise(activeStep, 'sets', exercise.sets + 1)}
                                                color="primary"
                                                size="large"
                                                sx={{ border: '1px solid', borderColor: 'divider' }}
                                            >
                                                +
                                            </IconButton>
                                        </Stack>
                                    </Box>

                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            {t('review.step.reps')}
                                        </Typography>
                                        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                                            <IconButton
                                                onClick={() => handleUpdateExercise(activeStep, 'reps', (parseInt(exercise.reps) || 0) - 1 > 0 ? ((parseInt(exercise.reps) || 0) - 1).toString() : "1")}
                                                color="primary"
                                                size="large"
                                                sx={{ border: '1px solid', borderColor: 'divider' }}
                                            >
                                                -
                                            </IconButton>
                                            <Typography variant="h4" fontWeight="bold">{parseInt(exercise.reps) || 0}</Typography>
                                            <IconButton
                                                onClick={() => handleUpdateExercise(activeStep, 'reps', ((parseInt(exercise.reps) || 0) + 1).toString())}
                                                color="primary"
                                                size="large"
                                                sx={{ border: '1px solid', borderColor: 'divider' }}
                                            >
                                                +
                                            </IconButton>
                                        </Stack>
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
                                            {t('review.step.recommended', { value: recommendation[index].reps })}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {t('review.step.notes')}
                                        </Typography>
                                        <Typography variant="body2">
                                            {exercise.notes}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>

                        {exercise.alternatives && exercise.alternatives.length > 0 && (
                            <Box sx={{ width: '100%', maxWidth: 400 }}>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<FitnessCenter />}
                                    onClick={handleSwapClick}
                                    sx={{ py: 1.5, mb: 2 }}
                                >
                                    {t('review.step.swap')}
                                </Button>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={handleSwapClose}
                                >
                                    {exercise.alternatives.map((alt) => (
                                        <MenuItem key={alt.exercise} onClick={() => handleSwapExercise(alt)}>
                                            <ListItemText
                                                primary={alt.exercise}
                                                secondary={`${alt.sets} sets x ${alt.reps}`}
                                            />
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </Box>
                        )}

                        <Box sx={{ width: '100%', maxWidth: 400 }}>
                            <Button
                                variant="text"
                                color="error"
                                fullWidth
                                startIcon={<DeleteIcon />}
                                onClick={() => handleToggleSkip()}
                            >
                                {t('review.step.skip')}
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
        );
    };

    const renderSummaryStep = () => (
        <Box sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" align="center" gutterBottom fontWeight="bold">
                {t('review.summary.ready')}
            </Typography>
            <Typography variant="body1" align="center" color="text.secondary" paragraph>
                {t('review.summary.subtitle', { count: editedWorkout.filter(e => !e.skipped).length })}
            </Typography>

            <Button
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                onClick={handleFinish}
                sx={{ mt: 4, maxWidth: 300 }}
            >
                {t('review.button.finish')}
            </Button>

            <Button
                size="small"
                onClick={onBack}
                sx={{ mt: 2 }}
            >
                {t('recommendation.generateNew')}
            </Button>
        </Box>
    );

    const renderContent = () => {
        if (activeStep === 0) return renderOverviewStep();
        if (activeStep === totalSteps - 1) return renderSummaryStep();
        return renderExerciseStep(activeStep - 1);
    };

    return (
        <Box sx={{ width: '100%', height: '100%', minHeight: 400, display: 'flex', flexDirection: 'column' }}>
            {activeStep > 0 && (
                <MobileStepper
                    variant="progress"
                    steps={totalSteps}
                    position="static"
                    activeStep={activeStep}
                    sx={{ flexGrow: 0, bgcolor: 'transparent' }}
                    nextButton={
                        <Button size="small" onClick={handleNext} disabled={activeStep === totalSteps - 1}>
                            {t('aiWizard.button.next')}
                            <KeyboardArrowRight />
                        </Button>
                    }
                    backButton={
                        <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
                            <KeyboardArrowLeft />
                            {t('aiWizard.button.back')}
                        </Button>
                    }
                />
            )}

            <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                {renderContent()}
            </Box>
        </Box>
    );
};
